/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import {
    Text,
    Flex,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig,
    useAccordionItemState,
} from '@chakra-ui/react'

import PropTypes from 'prop-types'
import SizeRefinements from './size-refinements'
import RadioRefinements from './radio-refinements'
// import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
// import {FILTER_ACCORDION_SATE} from '@salesforce/retail-react-app/app/constants'
//import ColorRefinements from './color-refinements'
import CheckboxRefinements from './checkbox-refinements'
import {PlusIcon, MinusIcon} from '../../../components/icons'
import {remCalc} from '../../../theme/_utils'
/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: CheckboxRefinements,
    // c_refinementColor: ColorRefinements,
    c_hexColour: CheckboxRefinements,
    c_size: SizeRefinements,
    price: RadioRefinements,
}

const Refinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    isOpen,
}) => {
    // Exclude filters in the exclude list.
    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    // const activeFilters = filters.filter((filter) => filter?.values)

    // Getting the indices of filters to open accordions by default
    let filtersIndexes = filters?.map((filter, idx) => idx)

    const [expandedIds, setExpandedIds] = useState([])

    const style = useStyleConfig('Refinements')

    const AccordionChevron = () => {
        const {isOpen} = useAccordionItemState()
        return isOpen ? (
            <Flex paddingY="xxsmall.base" paddingX="xxsmall.lg">
                <MinusIcon
                    width={remCalc(10.8)}
                    height={remCalc(10.8)}
                    color="foreground.neutral.subtle"
                />
            </Flex>
        ) : (
            <Flex paddingY="xxsmall.base" paddingX="xxsmall.lg">
                <PlusIcon
                    width={remCalc(10.8)}
                    height={remCalc(10.8)}
                    color="foreground.neutral.default"
                />
            </Flex>
        )
    }

    let expandedIndexes = filters
        ?.map((filter, index) => {
            if (expandedIds.includes(filter.attributeId)) {
                return index + 1
            } else {
                return null
            }
        })
        .filter(Boolean)

    if (expandedIds.includes('sortby')) {
        expandedIndexes = [0, ...expandedIndexes]
    }

    useEffect(() => {
        if (isOpen && filters && selectedFilters) {
            const toExpand = filters
                .filter((filter) => {
                    const selected = selectedFilters[filter.attributeId]
                    return Array.isArray(selected) ? selected.length > 0 : !!selected
                })
                .map((filter) => filter.attributeId)
            setExpandedIds(toExpand)
        }
    }, [isOpen])

    return (
        <Stack width="100%">
            {/* Wait to have filters before rendering the Accordion to allow the deafult indexes to be accurate */}
            {filtersIndexes && (
                <Accordion
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    onChange={(expandedIndex) => {
                        if (expandedIndex.includes(0)) {
                            setExpandedIds((prev) => [...prev, 'sortby'])
                        } else {
                            setExpandedIds((prev) => prev.filter((id) => id !== 'sortby'))
                        }
                    }}
                    opacity={isLoading ? 0.2 : 1}
                    allowMultiple={true}
                    index={expandedIndexes}
                    reduceMotion={true}
                    variant="filterAccordion"
                >
                    {itemsBefore}

                    {filters?.map((filter) => {
                        // Render the appropriate component for the refinement type, fallback to checkboxes
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                        // Catch any non-array values and wrap them in an array
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        // query-string parses "true"/"false" refine values into real booleans,
                        // but refinement option values are always strings, so normalize here.
                        selectedFiltersArray = selectedFiltersArray.map((value) =>
                            typeof value === 'boolean' ? String(value) : value,
                        )

                        const selectedValuesCount = selectedFiltersArray.filter(
                            (selectedValue) =>
                                selectedValue !== undefined &&
                                selectedValue !== null &&
                                selectedValue !== '',
                        ).length

                        return (
                            <React.Fragment key={filter.attributeId}>
                                <AccordionItem {...style?.item}>
                                    <>
                                        <AccordionButton
                                            paddingBottom={0}
                                            onClick={() => {
                                                setExpandedIds((prev) => {
                                                    const newExpandedIds = [...prev]
                                                    if (
                                                        newExpandedIds.includes(filter.attributeId)
                                                    ) {
                                                        return newExpandedIds.filter(
                                                            (id) => id !== filter.attributeId,
                                                        )
                                                    }
                                                    return [...newExpandedIds, filter.attributeId]
                                                })
                                            }}
                                            alignItems="center"
                                            _hover={{
                                                background: 'none',
                                                color: 'none',
                                            }}
                                        >
                                            {/*This is the main button */}
                                            <Flex
                                                width="100%"
                                                alignItems="center"
                                                justifyContent="flex-start"
                                            >
                                                <Text {...style?.itemButton}>
                                                    {filter.label}
                                                    {selectedValuesCount > 0 && (
                                                        <Text
                                                            as="span"
                                                            textStyle="body-small"
                                                            fontWeight="normal"
                                                            color="foreground.neutral.subtle"
                                                        >
                                                            {` (${selectedValuesCount})`}
                                                        </Text>
                                                    )}
                                                </Text>
                                            </Flex>
                                            {/* <AccordionIcon /> */}
                                            <AccordionChevron />
                                        </AccordionButton>
                                        <AccordionPanel width="100%" m={0} paddingLeft={0}>
                                            <Values
                                                selectedFilters={selectedFiltersArray}
                                                filter={filter}
                                                toggleFilter={toggleFilter}
                                            />
                                        </AccordionPanel>
                                    </>
                                </AccordionItem>
                            </React.Fragment>
                        )
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
}

export default Refinements
